
import { useEffect, useState } from 'react';

export const useTableSettings = (tableName: string, columnKeys: string[]) => {
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});

  useEffect(() => {
    try {
      // Load column visibility settings
      const savedVisibility = localStorage.getItem(`${tableName}-column-visibility`);
      if (savedVisibility) {
        setColumnVisibility(JSON.parse(savedVisibility));
      } else {
        // Default to all columns visible
        const defaultVisibility = columnKeys.reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {} as Record<string, boolean>);
        setColumnVisibility(defaultVisibility);
        localStorage.setItem(`${tableName}-column-visibility`, JSON.stringify(defaultVisibility));
      }

      // Load column width settings
      const savedWidths = localStorage.getItem(`${tableName}-column-widths`);
      if (savedWidths) {
        setColumnWidths(JSON.parse(savedWidths));
      }
    } catch (error) {
      console.error('Error loading table settings:', error);
      resetSettings();
    }
  }, [tableName, columnKeys]);

  const updateColumnVisibility = (newVisibility: Record<string, boolean>) => {
    try {
      setColumnVisibility(newVisibility);
      localStorage.setItem(`${tableName}-column-visibility`, JSON.stringify(newVisibility));
    } catch (error) {
      console.error('Error saving column visibility settings:', error);
    }
  };

  const updateColumnWidth = (columnKey: string, width: number) => {
    try {
      const newWidths = { ...columnWidths, [columnKey]: width };
      setColumnWidths(newWidths);
      localStorage.setItem(`${tableName}-column-widths`, JSON.stringify(newWidths));
    } catch (error) {
      console.error('Error saving column width settings:', error);
    }
  };

  const resetSettings = () => {
    try {
      // Reset to default visibility (all columns visible)
      const defaultVisibility = columnKeys.reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {} as Record<string, boolean>);
      
      setColumnVisibility(defaultVisibility);
      localStorage.setItem(`${tableName}-column-visibility`, JSON.stringify(defaultVisibility));
      
      // Reset column widths
      setColumnWidths({});
      localStorage.removeItem(`${tableName}-column-widths`);
    } catch (error) {
      console.error('Error resetting table settings:', error);
    }
  };

  return {
    columnVisibility,
    columnWidths,
    updateColumnVisibility,
    updateColumnWidth,
    resetSettings
  };
};
